/**
 * Modo de liquidação: o que o pagamento faz com o TÍTULO no settlement.
 *
 * `total_payment` é tri-state e não é status: é o sinal que vai na movimentação.
 *   true  → liquida o título, mesmo que o valor pago seja menor
 *   false → mantém aberto, mesmo que o valor pago seja maior
 *   null  → OPERATION_WATERFALL: o valor entra na OPERAÇÃO e desce em cascata pelas parcelas
 *           abertas, em ordem — cada uma coberta liquida e o resto transborda para a seguinte.
 *           É o que o time chama de "baldinho"; o identificador é em inglês, o rótulo de tela
 *           segue em português ("Amortizar Operação").
 *
 * Qual modo é válido depende do CENÁRIO do charge, e oferecer o errado gera movimentação errada.
 * Esta tabela espelha `billing/src/domain/charge_scenario.ALLOWED_MODES` — mudou lá, muda aqui.
 * Vive num módulo próprio porque três telas precisam dela (drill-in de faturas, geração de
 * fatura e Registrar Recebimento) e cópias divergem em silêncio.
 */
export type LiquidationMode = 'TOTAL' | 'PARTIAL' | 'OPERATION_WATERFALL';

export const MODE_TO_TOTAL_PAYMENT: Record<LiquidationMode, boolean | null> = {
    TOTAL: true, PARTIAL: false, OPERATION_WATERFALL: null,
};

/** Descrição do EFEITO — para quem só está lendo o que vai acontecer. */
export const MODE_LABELS: Record<LiquidationMode, string> = {
    TOTAL: 'Liquida o título',
    PARTIAL: 'Mantém o título aberto',
    OPERATION_WATERFALL: 'Amortiza a operação (distribui pelas parcelas)',
};

/**
 * A AÇÃO, no imperativo — para quem está escolhendo (opções de select, botões). Separado de
 * MODE_LABELS de propósito: "Liquida o título" descreve, "Liquidar" é o que se manda fazer, e a
 * mesma frase nos dois papéis lê mal em um dos dois. Os mesmos nomes do menu da tela de Títulos.
 */
export const MODE_ACTIONS: Record<LiquidationMode, string> = {
    TOTAL: 'Liquidar',
    PARTIAL: 'Baixa Parcial',
    OPERATION_WATERFALL: 'Amortizar Operação (distribui pelas parcelas)',
};

/** Cor de cada modo — as do menu da tela de Títulos e da paleta de cobrança. */
export const MODE_COLORS: Record<LiquidationMode, string> = {
    TOTAL: '#22c55e',
    PARTIAL: '#808000',
    OPERATION_WATERFALL: '#7c3aed',
};

/** O que o baldinho faz, para tooltip — não cabe no rótulo e não merece legenda fixa. */
export const WATERFALL_HINT =
    'O valor entra na operação e é distribuído pelas parcelas — não liquida esta parcela diretamente.';

/**
 * Modos aceitos ao PAGAR uma fatura.
 *
 * GUARANTEE aceita total e parcial desde que a garantia passou a ter TÍTULO próprio: o modo diz o
 * que acontece com o título de garantia. O que ela não oferece é OPERATION_WATERFALL — o rateio nas
 * parcelas da operação acontece nos dois casos, automaticamente, e oferecê-lo como escolha
 * sugeriria existir pagamento de garantia que NÃO amortiza a operação.
 */
export const PAYMENT_MODES_BY_SCENARIO: Record<string, LiquidationMode[]> = {
    RECEIVABLE: ['TOTAL', 'PARTIAL'],
    INSTALLMENT: ['TOTAL', 'PARTIAL', 'OPERATION_WATERFALL'],
    GUARANTEE: ['TOTAL', 'PARTIAL'],
};

/** Modos aceitos ao GERAR fatura — hoje idênticos aos do pagamento, cenário a cenário. */
export const DERIVE_MODES_BY_SCENARIO: Record<string, LiquidationMode[]> = {
    RECEIVABLE: ['TOTAL', 'PARTIAL'],
    INSTALLMENT: ['TOTAL', 'PARTIAL', 'OPERATION_WATERFALL'],
    GUARANTEE: ['TOTAL', 'PARTIAL'],
};

/** De volta ao modo, para pré-selecionar o que a fatura já declarava. */
export const modeOf = (totalPayment: boolean | null | undefined): LiquidationMode => (
    totalPayment === null ? 'OPERATION_WATERFALL' : totalPayment === false ? 'PARTIAL' : 'TOTAL'
);

/** O modo pedido, se o cenário aceitar; senão o primeiro que ele aceita. */
export const clampMode = (wanted: LiquidationMode, allowed: LiquidationMode[]): LiquidationMode => (
    allowed.includes(wanted) ? wanted : (allowed[0] || 'TOTAL')
);
